import React from 'react';
// @ts-ignore
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  ClipboardDocumentListIcon, 
  ChartBarIcon, 
  Cog6ToothIcon, 
  HeartIcon,
  UserGroupIcon,
  PlusCircleIcon,
  DocumentTextIcon,
  ChartPieIcon,
  ShoppingBagIcon,
  StarIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
// @ts-ignore
import { useAppTheme } from '@/context/ThemeContext';
import { useState, useEffect } from 'react';

type NavItem = {
  name: string;
  path: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  parameter?: number;
  children?: NavItem[];
  permissions?: string[];
};

type SidebarProps = {
  isOpen: boolean;
  isMinimized: boolean;
  onClose: () => void;
  theme?: string;
};

type NavItemProps = {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  isMinimized: boolean;
};

const businessNavigation: NavItem[] = [
  { name: 'Dashboard', path: '/business/dashboard', icon: HomeIcon, parameter: 1 },
  { 
    name: 'Products', 
    path: '/business/products', 
    icon: ShoppingBagIcon, 
    parameter: 2,
    children: [
      { name: 'All Products', path: '/business/products', icon: ShoppingBagIcon },
      { name: 'Add Product', path: '/business/products/add', icon: PlusCircleIcon },
      { name: 'Bulk Upload', path: '/business/products/bulk', icon: DocumentTextIcon }
    ]
  },
  { 
    name: 'Orders', 
    path: '/business/orders', 
    icon: ClipboardDocumentListIcon, 
    parameter: 3,
    children: [
      { name: 'All Orders', path: '/business/orders', icon: ClipboardDocumentListIcon },
      { name: 'Donation Requests', path: '/business/donation-requests', icon: HeartIcon }
    ]
  },
  { 
    name: 'Statistics', 
    path: '/business/statistics', 
    icon: ChartPieIcon, 
    parameter: 4, 
    children: [
      { name: 'Ratings & Reviews', path: '/business/statistics/ratings', icon: StarIcon },
      { name: 'Social Impact', path: '/business/statistics/social-impact', icon: HeartIcon },
      { name: 'Analytics', path: '/business/statistics/analytics', icon: ChartBarIcon }
    ]
  },
  { 
    name: 'Settings', 
    path: '/settings', 
    icon: Cog6ToothIcon, 
    parameter: 5,
    children: [
      { name: 'Profile Settings', path: '/settings/profile', icon: Cog6ToothIcon },
      { name: 'Business Settings', path: '/business/business-settings', icon: Cog6ToothIcon },
      { name: 'Staff Management', path: '/business/users', icon: UserGroupIcon }
    ]
  },
  {
    name: 'Help Center',
    path: '/business/help',
    icon: QuestionMarkCircleIcon,
    parameter: 6,
    children: [
      { name: 'Help Center', path: '/business/help', icon: QuestionMarkCircleIcon },
      { name: 'FAQ', path: '/business/help/faq', icon: QuestionMarkCircleIcon },
      { name: 'Report an Issue', path: '/business/help/report-issue', icon: ExclamationTriangleIcon },
      { name: 'Contact Support', path: '/business/help/contact-support', icon: ChatBubbleLeftRightIcon }
    ]
  },
];

// Charity navigation has been moved to CharitySidebar.tsx
// Dev navigation moved to DevSidebar.tsx

// NavItem component with dropdown support
const NavItemComponent: React.FC<NavItemProps> = ({ item, isActive, onClick, isMinimized }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { themeConfig } = useAppTheme();
  const location = useLocation();
  
  const hasActiveChild = item.children?.some(child => 
    location.pathname.startsWith(child.path)
  );
  
  const isItemActive = isActive || hasActiveChild;
  
  return (
    <div 
      className="px-3 py-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        if (!isHovered) setIsDropdownOpen(false);
      }}
    >
      {item.children ? (
        <div className="w-full">
          <button
            onClick={() => {
              onClick();
              setIsDropdownOpen(!isDropdownOpen);
            }}
            className="w-full cursor-pointer block"
          >
            <div
              className={`flex items-center justify-between px-2 py-2 rounded-lg transition-all duration-300 ease-in-out`}
              style={{
                backgroundColor: isItemActive 
                  ? themeConfig.accent
                  : isHovered 
                    ? themeConfig.surface 
                    : 'transparent'
              }}
            >
              <div className="flex items-center">
                <item.icon 
                  className="h-5 w-5 flex-shrink-0" 
                  style={{ 
                    color: isItemActive 
                      ? '#4A90E2'
                      : themeConfig.text
                  }}
                  aria-hidden="true" 
                />
                {!isMinimized && (
                  <span 
                    className="ml-3 text-sm font-medium"
                    style={{ 
                      color: isItemActive 
                        ? '#4A90E2'
                        : themeConfig.text
                    }}
                  >
                    {item.name}
                  </span>
                )}
              </div>
              {!isMinimized && item.children && (
                <ChevronDownIcon 
                  className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                  style={{ color: themeConfig.text }}
                />
              )}
            </div>
          </button>
          
          {!isMinimized && isDropdownOpen && (
            <div 
              className="mt-1 ml-4 space-y-1"
              style={{ backgroundColor: themeConfig.background }}
            >
              {item.children.map((child) => (
                <Link
                  key={child.name}
                  to={child.path}
                  className={`block px-2 py-2 rounded-lg text-sm transition-all duration-300 ease-in-out`}
                  style={{
                    backgroundColor: location.pathname.startsWith(child.path)
                      ? themeConfig.accent
                      : 'transparent',
                    color: location.pathname.startsWith(child.path)
                      ? themeConfig.primary
                      : themeConfig.text
                  }}
                >
                  <div className="flex items-center">
                    <child.icon 
                      className="h-4 w-4 flex-shrink-0" 
                      style={{ color: 'inherit' }}
                    />
                    <span className="ml-2">{child.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : (
        <Link
          to={item.path}
          onClick={onClick}
          className="w-full cursor-pointer block"
        >
          <div
            className={`flex items-center px-2 py-2 rounded-lg transition-all duration-300 ease-in-out`}
            style={{
              backgroundColor: isItemActive 
                ? themeConfig.accent
                : isHovered 
                  ? themeConfig.surface 
                  : 'transparent'
            }}
          >
            <item.icon 
              className="h-5 w-5 flex-shrink-0" 
              style={{ 
                color: isItemActive 
                  ? '#0c4a8a' // Darker blue for active icon
                  : themeConfig.text
              }}
              aria-hidden="true" 
            />
            {!isMinimized && (
              <span 
                className="ml-3 text-sm font-medium"
                style={{ 
                  color: isItemActive 
                    ? '#0c4a8a' // Darker blue for active text
                    : themeConfig.text
                }}
              >
                {item.name}
              </span>
            )}
          </div>
        </Link>
      )}
    </div>
  );
};

export default function Sidebar({ isOpen, isMinimized, onClose }: SidebarProps) {
  const location = useLocation();
  const { themeConfig } = useAppTheme();
  const [activeParameter, setActiveParameter] = useState<number>(1);
  
  // Only using business navigation in this component
  // Dev routes are handled by DevSidebar
  const navigation = businessNavigation;

  // Set active parameter based on location
  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = navigation.find(item => {
      if (item.path === '/' && currentPath === '/') return true;
      if (item.path !== '/' && currentPath.startsWith(item.path)) return true;
      return false;
    });
    
    if (activeItem?.parameter) {
      setActiveParameter(activeItem.parameter);
    }
  }, [location.pathname, navigation]);

  // Handle navigation item click
  const handleNavItemClick = (item: NavItem) => {
    setActiveParameter(item.parameter || 1);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/30 lg:hidden" 
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar container */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isMinimized ? 'lg:w-16' : 'lg:w-[240px]'}
          w-[240px] p-2`}
      >
        <div className="flex flex-col h-full rounded-xl overflow-hidden shadow-md border border-opacity-30"
          style={{
            backgroundColor: themeConfig.background,
            borderColor: '#4A90E2', // Fixed blue color for sidebar border
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
          }}
        >
          {/* Logo section */}
          <div className="flex items-center justify-center h-16 px-3">
            <img 
              src="/favicon.png" 
              alt="Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          {/* Divider */}
          <div className="h-[1px] w-full" style={{ backgroundColor: '#4A90E290' }}></div>

          {/* Navigation section */}
          <div className="flex-1 overflow-y-auto py-3">
            {/* Navigation items */}
            <div className="space-y-0.5">
              {navigation.map((item) => (
                <NavItemComponent
                  key={item.name}
                  item={item}
                  isActive={activeParameter === item.parameter}
                  onClick={() => handleNavItemClick(item)}
                  isMinimized={isMinimized}
                />
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
